import React, { useMemo, useState } from 'react';
import * as Yup from 'yup';

import Card from '@/components/Card';
import Form from '@/components/Form';
import FormField from '@/components/FormField';
import { TextInput } from '@/components/Inputs';
import SkeletonWrapper from '@/components/SkeletonWrapper';
import BillingFooter from '@/components/Billing/BillingFooter';
import Button from '@/components/Button';
import CouponInput from '@/components/Billing/CouponInput';

import { useDebounce } from '@/hooks';
import { useCheckoutContext } from '@/contexts';
import { toCurrencyString } from '@/number';

const defaultDebounceTime = 600;

const periodicityText = {
  monthly: 'mensalmente',
  quarterly: 'trimestralmente',
  half_yearly: 'semestralmente',
  yearly: 'anualmente'
};

const defaultValues = {
  users_count: 0
};

/* eslint-disable no-magic-numbers */
const schema = Yup.object().shape({
  users_count: Yup.number()
    .required('Número de usuários é obrigatório.')
});
/* eslint-enable no-magic-numbers */

function ChooseChatPlanAddons() {
  const {
    whatsappNumberAddon,
    setWhatsappNumberAddon,
    checkoutResumeData,
    period,
    nextStep
  } = useCheckoutContext();

  const whatsappNumberAddonPreview = checkoutResumeData.addonsInfo.find(
    (addonInfo) => addonInfo.productCode === 'whatsapp_number'
  );
  const currentValuePerItem = whatsappNumberAddonPreview
    ? whatsappNumberAddonPreview.monthlyValuePerItem
    : whatsappNumberAddon.defaultValue;

  const quantity = whatsappNumberAddon.quantity;

  const onChangeUsersCount = (value) => {
    setWhatsappNumberAddon({
      ...whatsappNumberAddon,
      quantity: value
    });
  };

  const onChangeCoupon = (coupon) => {
    setWhatsappNumberAddon({
      ...whatsappNumberAddon,
      discount_id: coupon?.billingDiscount.id
    });
  };

  const periodicity = checkoutResumeData.periodicity;

  const [showInput, setShowInput] = useState(false);

  const debounceHandler = useDebounce((fn) => fn(), defaultDebounceTime);

  const initialValues = useMemo(() => ({ users_count: quantity }), [quantity]);

  return (
    <>
      <Card className='p-6 border-light'>
        <Form
          name='addons-form'
          validationSchema={schema}
          defaultValues={defaultValues}
          initialValues={initialValues}
          onSubmit={nextStep}
        >
          {
            ({ handleSubmit }) => <>
              <h4 className='mb-6'>Número de WhatsApp</h4>

              <div className='text-dark-gray fw-bold mb-4'>
                Inclua números do WhatsApp extras conectadas à nossa API própria (via QR Code)
              </div>

              <div>
                <div className='fw-bold text-small text-dark-gray'>Número de WhatsApp</div>
                <FormField
                  as={TextInput}
                  name='users_count'
                  onChange={(value) => debounceHandler(() => onChangeUsersCount(Number(value)))}
                  className='max-width-5 mb-1'
                  type='number'
                  placeholder='4'
                  min='0'
                />
              </div>

              <SkeletonWrapper
                isLoading={!currentValuePerItem}
                SkeletonComponent={() => <div>Carregando</div>}
              >
                <div className='d-flex mb-1'>
                  <h1>{toCurrencyString(currentValuePerItem)}</h1>
                  <h1 className='mx-1'>/</h1>
                  <h4 className='fw-normal align-self-end mb-1'>por número</h4>
                </div>
              </SkeletonWrapper>

              <div className='fw-bold text-medium mt-2 mb-1'>
                Valor mensal, pago {periodicityText[periodicity]}
              </div>

              <BillingFooter
                buttonNextText='Avançar'
                onClickNext={handleSubmit}
              />
            </>
          }
        </Form>

        <Button
          className='mb-1 text-underline p-0 text-dark-gray text-small align-self-start'
          onClick={() => setShowInput(true)}
          variant='link'
        >
          Aplicar cupom
        </Button>

        {
          showInput &&
          <CouponInput
            usersCount={quantity}
            period={period}
            setCoupon={onChangeCoupon}
          />
        }
      </Card>
    </>
  );
}

export default ChooseChatPlanAddons;
